import { useState, useMemo, useEffect } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { subject } from '@casl/ability'
import { Container } from '../../components/Container/Container'
import { Menu } from '../../components/Menu/Menu'
import { Communities } from './Communities/Communities'
import { Vendors } from './Vendors/Vendors'
import { Commons } from './Commons/Commons'
import {
  excludeVendorAndMyOrganizationTab,
  MY_COMMUNITY_KEYS,
  MY_COMMUNITY_TABS,
  MY_COMMUNITY_TABS_COMMONS,
  MY_COMMUNITY_TABS_GPO
} from './constants'
import './styles.scss'
import { Gpo } from './Gpo/Gpo'
import { useSelector } from 'react-redux'
import { getUser } from '../../redux/store/user/getters'
import {
  ability,
  ACTIONS,
  Can,
  ROLES,
  SUBJECTS,
  SubjectWithAttributes
} from '../Permission'
import DownloadQRCode from '../HealthSystemDetails/DownloadQRCode'
import { MyOrganizationHealthSystemContainer } from './MyOrganizationHealthSystem/MyOrganizationHealthSystemContainer'
import useRouter from '../../hooks/useRouter'
import { VENDORS_TABS_OPTIONS } from './Vendors/constants'
import { GpoDetails } from './GpoDetails/GpoDetails'
import { MyOrganizationCommunityContainer } from './MyOrganizationCommunity/MyOrganizationCommunityContainer'
import { MyOrganizationGpo } from './MyOrganizationGpo/MyOrganizationGpo'
import { MyOrganizationCommons } from './MyOrganizationCommons/MyOrganizationCommons'
import { MyOrganizationVendor } from './MyOrganizationVendor/MyOrganizationVendor'

export const MyCommunity = () => {
  const { query, push } = useRouter()
  const { tab } = useParams()
  const [activeTab, setActiveTab] = useState('')

  const user = useSelector(getUser)

  const tabsOptions = useMemo(() => {
    let tabs
    if (user.role.includes('vendor')) {
      tabs = MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (excludeVendorAndMyOrganizationTab.includes(user.role)) {
      tabs = MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (user.role === ROLES.HOSPITAL_ADMIN) {
      tabs = MY_COMMUNITY_TABS.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.vendors
      )
    } else if (
      user.role === ROLES.COMMUNITY_PRESIDENT ||
      user.role === ROLES.VICE_PRESIDENT ||
      user.role === ROLES.CONTRACT_STEWARD ||
      user.role === ROLES.CLINICAL_COORDINATOR ||
      user.role === ROLES.COMMUNITY_RCA ||
      user.role === ROLES.LEGAL ||
      user.role === ROLES.ANALYST ||
      user.role === ROLES.HOSPITAL_USER ||
      user.role === ROLES.FINANCE
    ) {
      tabs = MY_COMMUNITY_TABS
    } else if (
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    ) {
      tabs = MY_COMMUNITY_TABS_GPO
    } else if (user.role.includes('commons')) {
      tabs =
        user.role === ROLES.COMMONS_VICE_PRESIDENT
          ? MY_COMMUNITY_TABS_COMMONS
          : MY_COMMUNITY_TABS_COMMONS?.filter(
              (tab) => tab.key !== MY_COMMUNITY_KEYS.gpo
            )
    } else {
      tabs = MY_COMMUNITY_TABS?.filter(
        (tab) => tab.key !== MY_COMMUNITY_KEYS.my_organization
      )
    }

    const canViewCommunities = ability.can(
      ACTIONS.VIEW,
      subject(SUBJECTS.COMMUNITIES_TAB, {
        ...user
      } as unknown as SubjectWithAttributes)
    )

    return tabs?.filter(
      (tab) => tab.key !== MY_COMMUNITY_KEYS.communities || canViewCommunities
    )
  }, [user])

  useEffect(() => {
    if (
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    ) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.my_organization)
    } else if (user.role.includes('commons')) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.my_organization)
    } else if (!user.gpo) {
      setActiveTab(tab || MY_COMMUNITY_KEYS.communities)
    } else {
      setActiveTab(MY_COMMUNITY_KEYS.gpo_details)
    }
  }, [tab, user])

  useEffect(() => {
    if (
      query.tab === VENDORS_TABS_OPTIONS[0].value ||
      query.tab === VENDORS_TABS_OPTIONS[1].value
    ) {
      setActiveTab(MY_COMMUNITY_KEYS.vendors)
    }
  }, [query])

  const isTabsVisible = useMemo(() => {
    return (
      !user.gpo ||
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER
    )
  }, [user])

  const defaultRoute = useMemo(() => {
    if (
      user?.role.includes('vendor') ||
      user.role === ROLES.COMMUNITY_PRESIDENT ||
      user.role === ROLES.VICE_PRESIDENT ||
      user.role === ROLES.CONTRACT_STEWARD ||
      user.role === ROLES.HOSPITAL_ADMIN ||
      user.role === ROLES.CLINICAL_COORDINATOR ||
      user.role === ROLES.GPO_PRESIDENT ||
      user.role === ROLES.GPO_FINANCE_OFFICER ||
      user.role === ROLES.COMMUNITY_RCA ||
      user.role === ROLES.LEGAL ||
      user.role === ROLES.ANALYST ||
      user.role === ROLES.HOSPITAL_USER ||
      user.role === ROLES.FINANCE ||
      user.role.includes('commons')
    ) {
      return MY_COMMUNITY_KEYS.my_organization
    } else if (user.role.includes('gpo')) {
      return MY_COMMUNITY_KEYS.gpo_details
    }
    return MY_COMMUNITY_KEYS.communities
  }, [user])

  const MyOrganizationRoute = useMemo(() => {
    const org = MY_COMMUNITY_KEYS.my_organization
    if (user.vendor) {
      return (
        <Route path={org} element={<MyOrganizationVendor />}>
          <Route path=":tab" element={<MyOrganizationVendor />} />
        </Route>
      )
    }
    if (user?.gpo) {
      return (
        <Route path={org} element={<MyOrganizationGpo />}>
          <Route path=":tab" element={<MyOrganizationGpo />} />
        </Route>
      )
    }
    if (user?.commons) {
      return (
        <Route path={org} element={<MyOrganizationCommons />}>
          <Route path=":tab" element={<MyOrganizationCommons />} />
        </Route>
      )
    }
    if (user?.outside_community) {
      return (
        <Route path={org} element={<MyOrganizationHealthSystemContainer />}>
          <Route
            path=":tab"
            element={<MyOrganizationHealthSystemContainer />}
          />
        </Route>
      )
    }
    return (
      <Route path={org} element={<MyOrganizationCommunityContainer />}>
        <Route path=":tab" element={<MyOrganizationCommunityContainer />} />
      </Route>
    )
  }, [user.vendor, user?.gpo, user?.commons, user?.outside_community])

  return (
    <Container
      title="My Community"
      menu={
        isTabsVisible ? (
          <Menu
            mode="horizontal"
            onSelect={(e) => {
              setActiveTab(e.key)
              push(e.key)
            }}
            selectedKeys={[activeTab]}
            defaultSelectedKeys={[activeTab]}
            items={tabsOptions}
          />
        ) : undefined
      }
      actions={
        user.outside_community && user.health_system ? (
          <div className="row align-center gap-8">
            <Can
              I={ACTIONS.DOWNLOAD}
              a={subject(SUBJECTS.QR_CODE, {
                uuid: user.health_system
              } as unknown as SubjectWithAttributes)}
            >
              <DownloadQRCode uuid={user.health_system} />
            </Can>
          </div>
        ) : undefined
      }
    >
      <Routes>
        <Route path={MY_COMMUNITY_KEYS.communities} element={<Communities />} />
        <Route path={MY_COMMUNITY_KEYS.gpo} element={<Gpo />} />
        <Route path={MY_COMMUNITY_KEYS.vendors} element={<Vendors />}>
          <Route path=":tab" element={<Vendors />} />
        </Route>
        <Route path={MY_COMMUNITY_KEYS.commons} element={<Commons />} />
        {MyOrganizationRoute}
        <Route path={MY_COMMUNITY_KEYS.gpo_details} element={<GpoDetails />} />
        <Route index element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </Container>
  )
}
